import {
  DownloadOutlined,
  FileExcelOutlined,
  FileOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileUnknownOutlined,
  FileWordOutlined,
  ShareAltOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import cn from 'classnames';
import DOMPurify from 'dompurify';
import { useRef, useState } from 'react';

import type { ModelClientFile } from '@app/api/ClientFiles';
import { useLazyDownloadQuery } from '@app/api/ClientFiles';
import {
  LEAD_MSG_STATUS_DELETED,
  formatLeadMessageText,
  formatLeadMessageTimestamp,
  getLeadMessageStatusColor,
  getLeadMessageStatusIcon,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { formatBytes } from '@app/utils';

import type { FC, ReactNode } from 'react';

import './LeadFileAttachment.scss';

const DOCUMENT_EXTENSIONS = new Set([
  'doc',
  'docx',
  'pdf',
  'txt',
  'xls',
  'xlsx',
]);

const getFileIcon = (extension: string, isUnsupported: boolean): ReactNode => {
  if (isUnsupported) return <FileUnknownOutlined />;

  switch (extension) {
    case 'doc':
    case 'docx':
      return <FileWordOutlined />;
    case 'pdf':
      return <FilePdfOutlined />;
    case 'txt':
      return <FileTextOutlined />;
    case 'xls':
    case 'xlsx':
      return <FileExcelOutlined />;
    default:
      return <FileOutlined />;
  }
};

const getFileMimeByExt = (extension: string): string => {
  switch (extension) {
    case 'pdf':
      return 'application/pdf';
    case 'txt':
      return 'text/plain';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return 'application/octet-stream';
  }
};

interface LeadFileAttachmentProps {
  file: ModelClientFile;
  message: string | null;
  socialType: string | null;
  isIncoming: boolean;
  timestamp: string;
  wasEdited: boolean;
  status: string | null;
  isForwarded: boolean;
  replyMessage: string | null;
  replyMessageId: number | null;
  showStatus?: boolean;
}

export const LeadFileAttachment: FC<LeadFileAttachmentProps> = ({
  file,
  message,
  socialType,
  isIncoming,
  timestamp,
  wasEdited,
  status,
  isForwarded,
  replyMessage,
  replyMessageId,
  showStatus = true,
}) => {
  const { params } = useUserPrint();
  const { showNotification } = useNotification();
  const [downloadFile] = useLazyDownloadQuery();

  const [isDownloading, setIsDownloading] = useState(false);
  const downloadAnchorRef = useRef<HTMLAnchorElement>(null);

  const extension = (file.Extension ?? '').trim().toLowerCase();
  const isUnsupported = !DOCUMENT_EXTENSIONS.has(extension);
  const fileName = file.FileName ?? `file-${file.Id}`;
  const fileSize = formatBytes(file.FileSize);
  const isDeleted = status === LEAD_MSG_STATUS_DELETED;
  const showStatusIcon = !isIncoming || isDeleted;
  const statusIcon = showStatusIcon ? getLeadMessageStatusIcon(status) : null;
  const statusColor = getLeadMessageStatusColor(status);
  const formattedTimestamp = formatLeadMessageTimestamp(timestamp);

  const sanitizedReplyHtml = replyMessage
    ? DOMPurify.sanitize(formatLeadMessageText(replyMessage, socialType))
    : null;
  const sanitizedMessageHtml = message
    ? DOMPurify.sanitize(formatLeadMessageText(message, socialType))
    : null;

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const buffer = await downloadFile({
        clientId: file.ClientId,
        fileId: file.Id,
        ActiveBranchId: params.ActiveBranchId,
      }).unwrap();

      const blob = new Blob([buffer], { type: getFileMimeByExt(extension) });
      const url = URL.createObjectURL(blob);

      if (downloadAnchorRef.current) {
        downloadAnchorRef.current.href = url;
        downloadAnchorRef.current.download = fileName;
        downloadAnchorRef.current.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      showNotification(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div
      className={cn('LeadFileAttachment', {
        'LeadFileAttachment--out': !isIncoming,
        'LeadFileAttachment--deleted': isDeleted,
        'LeadFileAttachment--unsupported': isUnsupported,
      })}
    >
      {isForwarded && (
        <div className="LeadFileAttachment__forwarded">
          <ShareAltOutlined className="LeadFileAttachment__forwarded-icon" />
          Пересланное сообщение
        </div>
      )}
      {sanitizedReplyHtml && (
        <div
          className="LeadFileAttachment__reply"
          data-reply-target-id={replyMessageId ?? undefined}
        >
          <div className="LeadFileAttachment__reply-indicator" />
          <div
            className="LeadFileAttachment__reply-text"
            dangerouslySetInnerHTML={{ __html: sanitizedReplyHtml }}
          />
        </div>
      )}
      <div className="LeadFileAttachment__file">
        <div className="LeadFileAttachment__icon">
          {getFileIcon(extension, isUnsupported)}
        </div>
        <div className="LeadFileAttachment__info">
          <Tooltip title={fileName}>
            <span className="LeadFileAttachment__name">{fileName}</span>
          </Tooltip>
          <div className="LeadFileAttachment__meta">
            <span>{extension || 'файл'}</span>
            <span>{fileSize}</span>
            {isUnsupported && <span>неизвестный тип вложения</span>}
          </div>
        </div>
        <Button
          type="text"
          size="small"
          icon={<DownloadOutlined />}
          loading={isDownloading}
          onClick={handleDownload}
          className="LeadFileAttachment__download-btn"
          aria-label="Скачать файл"
        />
      </div>
      {sanitizedMessageHtml && (
        <div
          className="LeadFileAttachment__text"
          dangerouslySetInnerHTML={{ __html: sanitizedMessageHtml }}
        />
      )}
      <a
        ref={downloadAnchorRef}
        className="LeadFileAttachment__download-anchor"
      />
      {showStatus && (
        <div className="LeadFileAttachment__status">
          <span className="LeadFileAttachment__timestamp">
            {wasEdited && 'изменено '}
            {formattedTimestamp}
          </span>
          {statusIcon && (
            <span
              className="LeadFileAttachment__status-icon"
              style={{ color: statusColor }}
            >
              {statusIcon}
            </span>
          )}
        </div>
      )}
    </div>
  );
};
